import {useEffect, useRef} from 'react';
import {toast} from '@kroll-resolver-crisp/ui-components-react';
import {useAcknowledgeFeedback, useMyPendingFeedback} from '@/api/hooks';

const toastId = (feedbackId: string) => `feedback-jira-${feedbackId}`;

/**
 Raises a sticky info toast for each piece of the analyst's own feedback that has been sent to
 Jira, on the same stack as every other toast. Closing it - Dismiss, the close button or a swipe -
 acknowledges it, so it does not come back on the next poll.

 The package's toast manager is an event emitter that drops anything raised before the Toaster
 subscribes, so this renders after `<Toaster>`, whose effects then run first.
 */
export function FeedbackJiraToast() {
  const {data: pending} = useMyPendingFeedback();
  const {mutate: acknowledge} = useAcknowledgeFeedback();
  const shown = useRef(new Set<string>());

  useEffect(() => {
    if (!pending) {
      return;
    }

    const current = new Set(pending.map((item) => toastId(item.id)));
    for (const item of pending) {
      const id = toastId(item.id);
      if (shown.current.has(id)) {
        continue;
      }

      shown.current.add(id);
      toast.add({
        id,
        type: 'info',
        timeout: 0,
        title: 'Your feedback is being looked into',
        description: item.message,
        actionProps: {
          children: 'Dismiss',
          onClick() {
            toast.close(id);
          }
        },
        onClose() {
          // Skipped when the toast is closed below because the poll already dropped it.
          if (shown.current.delete(id)) {
            acknowledge(item.id);
          }
        }
      });
    }

    // Acknowledged elsewhere (another tab): retire the notice without acknowledging it again.
    for (const id of shown.current) {
      if (!current.has(id)) {
        shown.current.delete(id);
        toast.close(id);
      }
    }
  }, [pending, acknowledge]);

  return null;
}
